import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Observable, Subject, debounceTime, takeUntil } from 'rxjs';
import { FilterService } from 'src/app/services/filter.service';
import { GeographicFilterModel } from '../../models/geographic-filter-model';
import { MultiSelectChangeEvent } from 'primeng/multiselect';

@Component({
  selector: 'ness-linked-agency-filter',
  templateUrl: './linked-agency-filter.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LinkedAgencyFilterComponent implements OnInit, OnDestroy {
  @Input() geoFilterVerbiage: string = 'Filter by:';
  @Input() geoFilterStorageKey: string = null;
  @Output() isLoading: EventEmitter<boolean> = new EventEmitter();

  geographicFilterModel: GeographicFilterModel = new GeographicFilterModel(null, null, [], null, null, [], 'all', [], null, null, null, null, null, null, 'All', 'All');
  agencies$: Observable<ServerModels.ENESS_REF_Agency[]> = this.filterService.agencies$.asObservable();

  // Subject observable to debounce multiselect dropdown components
  selectedAgencies: ServerModels.ENESS_REF_Agency[] = [];
  selectedAgenciesSubj$: Subject<ServerModels.ENESS_REF_Agency[]> = new Subject<ServerModels.ENESS_REF_Agency[]>();

  private _destroyed$ = new Subject<void>();

  constructor(public filterService: FilterService,
    private cdr: ChangeDetectorRef,
    public elementRef: ElementRef) { }

  ngOnInit(): void {
    this.geographicFilterModel = <GeographicFilterModel>JSON.parse(sessionStorage.getItem(this.geoFilterStorageKey));
    this.selectedAgencies = [...this.geographicFilterModel['_agencies']]  || [];
    this._setAgencyNames();
    this.filterService.applyGeoFilters(this.geoFilterStorageKey);
    this.filterService.geographicFilterModel.pipe(takeUntil(this._destroyed$)).subscribe((fm: GeographicFilterModel) => {
      if (!!fm && Object.keys(fm).length > 0) {
        this.geographicFilterModel = new GeographicFilterModel(null, null, [], null, null, [], fm['_agencyID'], fm['_agencies'] || [], null, null, null, null, null, null, fm['_agencyName'], fm['_agencyNamesList']);
        this.selectedAgencies = [...fm['_agencies']];
        this.cdr.detectChanges();
      }
    });

  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
    this.selectedAgenciesSubj$.next([]);
    this.selectedAgenciesSubj$.complete();
  }

  private _setAgencyNames(): void {
    this.filterService.agencyNameList$.next(this.geographicFilterModel.agencyNamesList);
    this.filterService.agencyNames$.next(this.geographicFilterModel.agencyName);
  }

  public onSelectAgencies(evt: MultiSelectChangeEvent): void {
    this.isLoading.emit(true);
    if (!this.selectedAgenciesSubj$.observed) {
      this.selectedAgenciesSubj$.pipe(debounceTime(1000), takeUntil(this._destroyed$)).subscribe({
        next: (agencies: ServerModels.ENESS_REF_Agency[]) => {
          this.selectedAgencies = [...agencies];
          let agencyIds: string = agencies.map(x => x.AgencyID)?.join(';');

          if (agencies?.length > 0 && agencies?.length != this.filterService.agencies$.getValue()?.length) {
            this.geographicFilterModel.agencyNamesList = '';
            agencies.forEach(agency => this.geographicFilterModel.agencyNamesList += `${agency.AgencyName}; `);
          } else {
            this.geographicFilterModel.agencyNamesList = 'All';
            agencyIds = 'all';
          }

          this.geographicFilterModel = new GeographicFilterModel(null, null, [], null, null, [], agencyIds, agencies || [], null, null, null, null, null, null, this.geographicFilterModel.agencyName, this.geographicFilterModel.agencyNamesList);

          this.filterService.geographicFilterModel$.next(this.geographicFilterModel);
          this.isLoading.emit(false);
        },
        error: (err) => this.isLoading.emit(false)
      });
    }

    this.selectedAgenciesSubj$.next(evt.value);
  }

  public applyFilters(): void {
    sessionStorage.setItem(this.geoFilterStorageKey, JSON.stringify(this.geographicFilterModel));
    this._setAgencyNames();
    this.filterService.applyGeoFilters(this.geoFilterStorageKey);
  }

  public resetToDefaultFilters(): void {
    this.filterService.resetGeoFilterDefaults(this.geoFilterStorageKey);
  }
}
